import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const MessageContext = createContext({
  showMessage: async () => {},
  errorConfigMessage: async () => {},
  showQuestion: async () => false,
});

function MessageProvider({ children }) {
  // Stato per gestire question e messaggi a livello globale
  const [question, setQuestion] = useState(null);
  const [message, setMessage] = useState(null);
  const questionResolver = useRef(null);
  const messageResolver = useRef(null);
  const messageTimer = useRef(null);

  // Ripulisce eventuali resolver appesi quando chiudiamo la modale question
  const cleanupQuestion = () => {
    if (questionResolver.current) {
      questionResolver.current = null;
    }
    setQuestion(null);
  };

  // Cancella timer e resolver legati al messaggio toast
  const cleanupMessage = () => {
    if (messageTimer.current) {
      clearTimeout(messageTimer.current);
      messageTimer.current = null;
    }
    if (messageResolver.current) {
      messageResolver.current = null;
    }
    setMessage(null);
  };

  useEffect(() => () => {
    if (messageTimer.current) {
      clearTimeout(messageTimer.current);
    }
  }, []);

  // Espone una modale di conferma e restituisce una Promise risolta con true/false
  const showQuestion = useCallback((options = {}) => {
    const {
      title = "Conferma",
      message: body,
      confirmLabel = "OK",
      cancelLabel = "Annulla",
    } = options;

    if (!body) {
      return Promise.resolve(false);
    }

    if (questionResolver.current) {
      questionResolver.current(false);
    }

    return new Promise((resolve) => {
      questionResolver.current = resolve;
      setQuestion({ title, body, confirmLabel, cancelLabel });
    });
  }, []);

  // Mostra un messaggio (toast/modal) con tonalità e timeout opzionale
  const showMessage = useCallback((options = {}) => {
    const {
      title = "Messaggio",
      message: body,
      type = "info",
      timeout = 0,
      confirmLabel = "OK",
      code = null,
      details = null,
    } = options;

    if (!body) {
      return Promise.resolve();
    }

    if (messageResolver.current) {
      messageResolver.current();
    }
    if (messageTimer.current) {
      clearTimeout(messageTimer.current);
      messageTimer.current = null;
    }

    return new Promise((resolve) => {
      messageResolver.current = resolve;
      setMessage({ title, body, type, timeout, confirmLabel, code, details });
      if (timeout > 0) {
        messageTimer.current = setTimeout(() => {
          resolve();
          cleanupMessage();
        }, timeout * 1000);
      }
    });
  }, []);

  const errorConfigMessage = useCallback((options = {}) => {
    const {
      title = "Errore configurazione",
      type = "warning",
      ...rest
    } = options;

    return showMessage({
      title,
      type,
      ...rest,
    });
  }, [showMessage]);

  // Gestori per i pulsanti della question
  const confirm = (accepted) => {
    if (questionResolver.current) {
      questionResolver.current(Boolean(accepted));
    }
    cleanupQuestion();
  };

  // Chiude manualmente il messaggio (quando non è auto-dismiss)
  const closeMessage = () => {
    if (messageResolver.current) {
      messageResolver.current();
    }
    cleanupMessage();
  };

  // Context value esposto a tutta l'app
  const value = {
    showMessage,
    errorConfigMessage,
    showQuestion,
  };

  const toneStyles = {
    info: {
      badge: { backgroundColor: "#dbeafe", borderColor: "#93c5fd", color: "#1e3a8a" },
      panel: { backgroundColor: "#eff6ff", borderColor: "#93c5fd", color: "#172554", boxShadow: "0 24px 60px rgba(37,99,235,0.18)" },
      technicalPanel: { backgroundColor: "#eff6ff", borderColor: "#93c5fd", color: "#0f172a", boxShadow: "0 24px 60px rgba(37,99,235,0.18)" },
      body: { color: "#172554" },
      technicalBody: { backgroundColor: "#ffffff", borderColor: "#cbd5e1", color: "#1f2937" },
      subtle: { color: "rgba(30, 64, 175, 0.8)" },
      details: { backgroundColor: "#ffffff", borderColor: "#bfdbfe", color: "#172554" },
      button: { backgroundColor: "#1d4ed8", borderColor: "#93c5fd", color: "#ffffff" },
    },
    success: {
      badge: { backgroundColor: "#dcfce7", borderColor: "#86efac", color: "#166534" },
      panel: { backgroundColor: "#f0fdf4", borderColor: "#86efac", color: "#14532d", boxShadow: "0 24px 60px rgba(22,163,74,0.18)" },
      technicalPanel: { backgroundColor: "#f0fdf4", borderColor: "#86efac", color: "#0f172a", boxShadow: "0 24px 60px rgba(22,163,74,0.18)" },
      body: { color: "#14532d" },
      technicalBody: { backgroundColor: "#ffffff", borderColor: "#cbd5e1", color: "#1f2937" },
      subtle: { color: "rgba(22, 101, 52, 0.8)" },
      details: { backgroundColor: "#ffffff", borderColor: "#bbf7d0", color: "#14532d" },
      button: { backgroundColor: "#15803d", borderColor: "#86efac", color: "#ffffff" },
    },
    warning: {
      badge: { backgroundColor: "#fef3c7", borderColor: "#fcd34d", color: "#92400e" },
      panel: { backgroundColor: "#fffbeb", borderColor: "#fcd34d", color: "#78350f", boxShadow: "0 24px 60px rgba(217,119,6,0.18)" },
      technicalPanel: { backgroundColor: "#fffbeb", borderColor: "#fcd34d", color: "#0f172a", boxShadow: "0 24px 60px rgba(217,119,6,0.18)" },
      body: { color: "#78350f" },
      technicalBody: { backgroundColor: "#ffffff", borderColor: "#e5e7eb", color: "#1f2937" },
      subtle: { color: "rgba(146, 64, 14, 0.8)" },
      details: { backgroundColor: "#ffffff", borderColor: "#fde68a", color: "#78350f" },
      button: { backgroundColor: "#d97706", borderColor: "#fcd34d", color: "#ffffff" },
    },
    error: {
      badge: { backgroundColor: "#fee2e2", borderColor: "#fca5a5", color: "#991b1b" },
      panel: { backgroundColor: "#fef2f2", borderColor: "#fca5a5", color: "#7f1d1d", boxShadow: "0 24px 60px rgba(220,38,38,0.18)" },
      technicalPanel: { backgroundColor: "#fef2f2", borderColor: "#fca5a5", color: "#0f172a", boxShadow: "0 24px 60px rgba(220,38,38,0.18)" },
      body: { color: "#7f1d1d" },
      technicalBody: { backgroundColor: "#ffffff", borderColor: "#e5e7eb", color: "#1f2937" },
      subtle: { color: "rgba(153, 27, 27, 0.8)" },
      details: { backgroundColor: "#ffffff", borderColor: "#fecaca", color: "#7f1d1d" },
      button: { backgroundColor: "#b91c1c", borderColor: "#fca5a5", color: "#ffffff" },
    },
  };

  const formatDetails = (details) => {
    if (!details) return null;
    if (typeof details === "string") return details;
    try {
      return JSON.stringify(details, null, 2);
    } catch (_) {
      return String(details);
    }
  };

  return (
    <MessageContext.Provider value={value}>
      {children}
      {question && (
        <div className="fixed inset-0 z-[2000] flex items-start justify-center pt-16 bg-slate-950/40 backdrop-blur-[2px]">
          <div className="pointer-events-auto w-full max-w-xl px-4">
            <div className="rounded-2xl border border-blue-300 bg-blue-50 p-6 text-blue-950 shadow-[0_24px_60px_rgba(37,99,235,0.18)]">
              <div className="text-center mb-4">
                <div className="text-sm uppercase tracking-wide text-blue-900 font-semibold">
                  {question.title}
                </div>
                <div className="mt-2 text-lg font-bold text-blue-950">
                  {question.body}
                </div>
              </div>
              <div className="flex justify-center gap-4 mt-6">
                <button
                  className="px-5 py-2 rounded-lg border border-blue-200 text-blue-900 font-semibold bg-white/90 hover:bg-white"
                  onClick={() => confirm(false)}
                >
                  {question.cancelLabel}
                </button>
                <button
                  className="px-5 py-2 rounded-lg bg-blue-700 text-white font-semibold shadow hover:bg-blue-800"
                  onClick={() => confirm(true)}
                >
                  {question.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {message && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/55">
          <div className="pointer-events-auto w-full max-w-lg px-4">
            {(() => {
              const tone = toneStyles[message.type] || toneStyles.info;
              const technicalMessage = Boolean(
                message.code ||
                message.details ||
                (typeof message.body === "string" && message.body.includes("\n"))
              );
              const formattedDetails = formatDetails(message.details);

              return (
                <div
                  className="rounded-2xl border px-6 py-6"
                  style={technicalMessage ? tone.technicalPanel || tone.panel : tone.panel}
                >
                  <div className="text-center">
                    <div
                      className="inline-flex items-center rounded-full border px-3 py-1 text-xs uppercase tracking-wide font-semibold"
                      style={tone.badge}
                    >
                      {message.title}
                    </div>
                  </div>
                  <div
                    className={technicalMessage ? "mt-4 rounded-xl border px-4 py-4 text-base font-semibold whitespace-pre-line leading-6 text-left" : "mt-4 text-xl font-bold text-center"}
                    style={technicalMessage ? tone.technicalBody || tone.body : tone.body}
                  >
                    {message.body}
                  </div>
                  {message.code && (
                    <div className="mt-4 text-left text-xs tracking-wide font-mono" style={tone.subtle}>
                      Codice: {message.code}
                    </div>
                  )}
                  {formattedDetails && (
                    <pre
                      className="mt-3 max-h-40 overflow-auto rounded-lg border px-3 py-2 text-left text-xs leading-5 whitespace-pre-wrap break-all"
                      style={tone.details}
                    >
                      {formattedDetails}
                    </pre>
                  )}
                  {message.timeout === 0 && (
                    <div className="mt-6 flex justify-center">
                      <button
                        className="inline-flex min-w-[7rem] items-center justify-center px-6 py-2 rounded-lg font-semibold border focus:outline-none transition shadow-sm"
                        style={tone.button}
                        onClick={closeMessage}
                      >
                        {message.confirmLabel || "OK"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </MessageContext.Provider>
  );
}

export function useMessage() {
  return useContext(MessageContext);
}

export default MessageProvider;
